<template>
  <div ref="modal" class="ai-generate-modal" :style="positionStyle" @click.stop>
    <div class="ai-generate-container bg-white">
      <!-- Header -->
      <AIGenerateHeader ref="header" @close="handleClose" @drag-start="dragMouseDown" />
      <!-- Chat Body -->
      <div ref="chatBody" class="ai-generate-body d-flex flex-column bg-light px-gd-3 py-gd-2 w-100 gap-gd-2">
        <!-- Messages -->
        <AIGenerateMessageItem
          v-for="(msg, index) in messages"
          :key="index"
          :message="msg"
          @copy="handleCopy"
          @apply="handleApply"
        />

        <!-- Loading indicator -->
        <AIGenerateLoading v-if="showLoadingMessage" />
      </div>

    <div class="ai-generate-control d-flex flex-column gap-gd-2 p-gd-2 w-100" :class="{disabled: isLoading}">
        <!-- ========== INPUT SECTION (only in input mode) ========== -->
        <!-- Input Area -->
          <AIGenerateInput 
            ref="inputComponent"
            :show="true"
            :is-loading="isLoading"
            @send="handleSend"
          />

        <template v-if="controlMode === 'input'">
          <!-- Tone Selector and Credits -->
          <div class="d-flex align-items-center justify-content-between">
           <AIGenerateToneSelector
            :is-loading="isLoading"
            @tone-selected="handleToneSelected"
          />

            <!-- Credits Display -->
           <AIGenerateCreditDisplay :msg-assistant="messageAssistant" />
          </div>          

          <!-- Suggestion Tags -->
          <b-skeleton-wrapper :loading="isLoadingAPI.suggest" :row="1" :col="1">
            <template #loading>
              <hr class="border-gray-light -mx-gd-2 my-gd-1"/>
              <b-skeleton type="button" height="32px" width="100px" class="border-radius-rounder"></b-skeleton>
            </template>
            <template #default>
              <div v-if="createSuggestionTags && createSuggestionTags.length > 0">
                <hr class="border-gray-light -mx-gd-2 my-gd-1"/>
                <AIGenerateSuggestions 
                  :suggestion-tags="createSuggestionTags"
                  :is-loading="isLoading"
                  :selected-tag-id="selectedSuggestionTagId"
                  @suggestion-click="handleSuggestionClick"
                />
              </div>
            </template>
          </b-skeleton-wrapper>
        </template>

        <!-- ========== ACTIONS (hidden in input mode) ========== -->
        <template v-if="controlMode === 'actions'">
          <AIGenerateActions
            :revise-tags="createReviseTags"
            :is-loading="isLoading"
            @action-click="handleActionClick"
          />

          <!-- Language Selector Footer -->
          <AIGenerateFooter :msg-assistant="messageAssistant" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AIGenerateHeader from './AIGenerateHeader.vue';
import AIGenerateMessageItem from './AIGenerateMessageItem.vue';
import AIGenerateLoading from './AIGenerateLoading.vue';
import AIGenerateSuggestions from './AIGenerateSuggestions.vue';
import AIGenerateInput from './AIGenerateInput.vue';
import AIGenerateToneSelector from './AIGenerateToneSelector.vue';
import AIGenerateActions from './AIGenerateActions.vue';
import AIGenerateFooter from './AIGenerateFooter.vue';
import AIGenerateCreditDisplay from './AIGenerateCreditDisplay.vue';
import aiGenerateService from '@services/aiGenerateService.js';

export default {
  name: 'AIGenerateModal',
  components: {
    AIGenerateHeader,
    AIGenerateMessageItem,
    AIGenerateLoading,
    AIGenerateSuggestions,
    AIGenerateInput,
    AIGenerateToneSelector,
    AIGenerateActions,
    AIGenerateFooter,
    AIGenerateCreditDisplay,
  },
  props: {
    position: {
      type: Object,
      default: () => ({ top: '0px', left: '0px', right: '0px' })
    },
    actionContext: {
      type: Object,
      default: () => ({})
    },
    originalText: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      messages: [],
      isLoading: false,
      isLoadingAPI : {
        detail: false,
        generate: false,
        revise: false,
        translate: false,
        suggest: false,
      },
      translate : {
        old : undefined,
        new : undefined,
      },
      selectedLanguage: null,
      selectedTone: null,
      selectedSuggestionTagId: null,
      messageCount: 0,
      /**
       * controlMode determines which UI is shown in the bottom control area:
       * - 'input'   : Input field + Tone selector + Suggestions (create mode)
       * - 'actions'  : Action buttons + Disclaimer + Credits (after AI responds)
       */
      controlMode: 'input',
      /** Suggestion tags fetched from API */
      suggestionTags: [],
      /** Revise tags fetched from API */
      reviseTags: [],
      /** Session ID from AI generate API */
      sessionId: '',
      /** Polling timer reference */
      pollingTimer: null,
      /** Polling interval in ms */
      POLLING_INTERVAL: 5000,
      actionType: null,
      actionSelect : null,
      /** Track if user has copied or applied any AI message */
      hasUsedMessage: false,
      // Drag position tracking
      pos1: 0,
      pos2: 0,
      pos3: 0,
      pos4: 0,
    };
  },
  computed: {
    ...mapGetters({
      languages: 'designIframe/page/languages',
      languageUse: "designIframe/metadata/siteActiveLanguage",
      languageEngine: 'designIframe/userPreference/languageEngine',
      prompt: 'designIframe/ai/prompt',
      creditDetailAI: 'designIframe/credit/creditDetailAI',
      isLoadingAiCredit: 'designIframe/credit/isLoadingAiCredit'
      ,
    }),
    /** Current mode derived from actionContext */
    currentMode() {
      return this.actionContext && this.actionContext.parent && this.actionContext.parent.id ? this.actionContext.parent.id : 'create';
    },
    positionStyle() {
      return {
        top: this.position.top,
        left: this.position.left,
      };
    },
    /** Show revise tags when there are tags and not loading */
    showReviseTags() {
      return this.reviseTags.length > 0;
    },
    /** Suggestion tags for create/input mode (from API) */
    createSuggestionTags() {
      return this.suggestionTags && this.suggestionTags.map(item => ({
        id: String(item.id),
        labelDirect: item.name,
        prompt: item.prompt,
        action: 'fill_input',
      }));
    },
    /** Revise tags for create/input mode (from API) */
    createReviseTags() {
      return this.reviseTags.map(item => ({
        id: String(item.id),
        labelDirect: item.name,
        prompt: item.prompt,
        data: item.data || null,
        action: 'fill_input',
      }));
    },
    availableLanguages() {
      let langs = [];
      if (this.languages) {
        Object.keys(this.languages).forEach(key => {
          const langData = this.languages[key];
          if (!langs.find(l => l.id === key)) {
            langs.push({
              id: key,
              label: langData.language && langData.language.toUpperCase() ? langData.language.toUpperCase() : key,
              fullLabel: langData.language && langData.language.toUpperCase() ? langData.language.toUpperCase() : key,
              flagIcon: langData.flagIcon
            });
          }
        });
      }
      return langs;
    },
    creditAvailable() {
      return this.creditDetailAI.available || 0;
    },
    creditUsed() {
      return this.creditDetailAI.creditUsed || 0;
    },
    creditRemaining() {
      return this.creditDetailAI.creditRemaining || 0;
    },
    showLoadingMessage(){
      return this.isLoadingAPI.detail || false
    },
    messageAssistant() {
      return this.messages.filter(msg => msg.role === 'assistant') || [];
    }
  },
  mounted() {
    if (this.visible) {
      this.initModal(true);
    }
    document.addEventListener("mouseleave", this.detectOutOfFrame, { passive: true });
    this._clickOutsideTimer = setTimeout(() => {
      document.addEventListener('mousedown', this.handleClickOutside, true);
    }, 150);
  },
  methods: {
    getTagLabel(tag) {
      if(tag.name) return tag.name;
      if (tag.labelDirect) return tag.labelDirect;
      return this.$t(tag.label, this.languageEngine);
    },
    async fetchSuggestPrompt() {
      this.isLoadingAPI.suggest = true;
      if(this.prompt.suggestion === undefined) {
        await this.$store.dispatch('designIframe/ai/fetchPrompt', { type: 'text', subType: 'suggestion' });
      }
      this.suggestionTags = this.prompt.suggestion || [];
      this.isLoadingAPI.suggest = false;
    },
    async getCreditDetail() {
      await new Promise(resolve => {
        this.$store.dispatch('designIframe/credit/fetchCreditDetail', { type: 'ai' }).then(() => {
          resolve(true);
        });
      });
    },
    async checkCredit() {
      const payloadsType = {
        'GENERATE_TEXT': ['create'],
        'REVISE_TEXT': ['shorten', 'expand', 'tone','edit', 'revise'],
        'TRANSLATE_TEXT': ['translate'],
      }
      const payloadType = Object.keys(payloadsType).find(key => payloadsType[key].includes(this.actionType)) || 'create';
      const promptInput = {
        type: payloadType,
        isGenImage: false
      };
      await this.$store.dispatch('designIframe/credit/postCreditCheck', { type: 'ai', promptInput }).catch(() => { return null });
      return this.hasEnoughCredits();
    },
    async initModal(isFirstInit = false) {
      this.stopPolling();
      this.messages = [];
      this.isLoading = false;
      this.messageCount = 0;
      this.selectedSuggestionTagId = null;
      this.sessionId = '';
      this.hasUsedMessage = false;
      this.selectedLanguage = this.languageUse;
      this.translate.old = this.languageUse;
      this.translate.new = this.languageUse;

      const parentId = this.actionContext && this.actionContext.parent.id && this.actionContext.parent.id !== 'create' ? this.actionContext.parent.id : 'create';
      const subOptionId = this.actionContext && this.actionContext.subOption && this.actionContext.subOption.id ? this.actionContext.subOption.id : null;
      this.actionSelect = parentId;

      // Mode-specific initialization
      if(!isFirstInit) {
        await this.getCreditDetail();
      }
      switch (parentId) {
        case 'create':
          // Input mode: user types prompt
          this.setControlMode('input');
          this.actionType = 'create';
          this.fetchSuggestPrompt();
          this.$nextTick(() => {
            this.resetInputComponent();
            this.sessionId = null;
            this.messages = [];
            this.messages.push({
              role: 'assistant',
              text: this.$t('engine.aiGenerate.lblDescription', this.languageEngine),
              isSystemMessage: true,
            });
          });
          break;

        case 'shorten':
          // Actions mode: auto-send shorten
          this.setControlMode('actions');
          this.actionType = 'revise';
          this.$nextTick(() => {
            this.autoSendAction(
              this.$t('engine.aiGenerate.prompt.shorten', this.languageEngine),
              this.$t('engine.aiGenerate.prompt.shortenSubLabel', this.languageEngine)
            );
          });
          break;

        case 'expand':
          // Actions mode: auto-send expand
          this.setControlMode('actions');
          this.actionType = 'revise';
          this.$nextTick(() => {
            this.autoSendAction(
              this.$t('engine.aiGenerate.prompt.expand', this.languageEngine),
              this.$t('engine.aiGenerate.prompt.expandSubLabel', this.languageEngine)
            );
          });
          break;

        case 'tone':
          this.setControlMode('actions');
          if (subOptionId) {
            this.actionType = 'revise';
            const toneLabel = this.actionContext.subOption.label;
            const toneName = this.$t(toneLabel, this.languageEngine);
            this.$nextTick(() => {
              this.autoSendAction(
                this.$t('engine.aiGenerate.prompt.tone', this.languageEngine).replace('{tone}', toneName),
                this.$t('engine.aiGenerate.prompt.toneSubLabel', this.languageEngine).replace('{tone}', toneName)
              );
            });
          }
          break;

        case 'translate':
          this.setControlMode('actions');
          if (subOptionId) {
            this.actionType = 'translate';
            const langLabel = this.actionContext.subOption.label;
            const targetLanguage = this.actionContext.subOption.id;
            this.translate.new = targetLanguage;
            this.$nextTick(() => {
              this.autoSendAction(
                this.$t('engine.aiGenerate.prompt.translate', this.languageEngine).replace('{language}', langLabel),
                this.$t('engine.aiGenerate.prompt.translateSubLabel', this.languageEngine).replace('{language}', langLabel),
                targetLanguage
              );
            });
          }
          break;

        default:
          this.setControlMode('input');
          this.actionType = 'create';
          this.fetchSuggestPrompt();
          this.$nextTick(() => {
            this.resetInputComponent();
            this.sessionId = null;
            this.messages = [];
            this.messages.push({
              role: 'assistant',
              text: this.$t('engine.aiGenerate.lblDescription', this.languageEngine),
              isSystemMessage: true,
            });
          });
          break;
      }
    },

    async autoSendAction(promptText, subLabel, targetLanguage = null) {
      this.isLoading = true;
      if (!await this.checkCredit()) {
          this.showInsufficientCreditModal();
          this.isLoading = false;
          return false;
      }

      if (this.actionType === 'create') {
        await this.generateText(promptText);
      }else{
        const messageLastAssistant = this.getLastAssistantMessage();
        const originalText = messageLastAssistant && messageLastAssistant.text ? messageLastAssistant.text : this.originalText || '';
        if (this.actionType === 'translate' && targetLanguage) {
          await this.translateGenerateText(originalText, targetLanguage);
        } else {
          this.actionSelect = subLabel;
          await this.reviseGenerateText(promptText, this.actionSelect, originalText);
        }
      }
      await this.addUserMessage(promptText, subLabel);
    },

    async handleSend(text) {
      if (!text || this.isLoading) return false;
      this.isLoading = true;
      
      if (!await this.checkCredit()) {
        this.showInsufficientCreditModal();
        this.isLoading = false;
        return false;
      }
      if(this.actionType === 'create' && this.sessionId == null) {
        await this.generateText(text);
      }else{
        this.actionSelect = 'edit';
        this.actionType = 'revise';
        await this.reviseGenerateText(text, this.actionSelect);
      }

      await this.addUserMessage(text);
    },

    addUserMessage(text, subLabel = '') {
      this.messageCount++;
      const message = {
        role: 'user',
        text: text,
        count: this.messageCount,
      };
      if (subLabel !== undefined) {
        message.subLabel = subLabel;
      }
      this.messages.push(message);
    },

    getLastAssistantMessage() {
      return this.messages.filter(msg => msg.role === 'assistant').slice(-1)[0];
    },

    async generateText(promptText) {
      this.isLoadingAPI.generate = true;
      this.stopPolling();
      
      const payload = {
        sessionId: this.sessionId || null, // create
        prompt: promptText, // create
        language: this.selectedLanguage || this.languageUse, //create
        contentTone: this.selectedTone || null,  //create
      };

      const response = await aiGenerateService.postGenerateText(payload)
      if (response && response.success && response.data) {
        this.sessionId = response.data.sessionId || '';
        // Start polling for the result
        if (this.sessionId) {
          this.startPolling();
        } else {
          this.receiveError();
        }
      } else {
        this.receiveError(response.message || '');
      }
      this.getCreditDetail();
      this.isLoadingAPI.generate = false;
      return true;
    },
    async reviseGenerateText(promptText, promptName, originalText = '') {
      this.isLoadingAPI.revise = true;
      this.stopPolling();

      const payload = {
        sessionId: this.sessionId, // revise
        prompt: promptText, // revise
        fullText: originalText || promptText || '', //revise
        selectedText: originalText || promptText || '', //revise
        promptName: promptName || this.actionSelect || this.currentMode || '', //revise
        language: this.translate.old || this.selectedLanguage || this.languageUse, //revise
        contentTone: this.selectedTone || null, //revise
      };
      const response = await aiGenerateService.postReviseGenerateText(payload).catch(() => { return null})
      if (response && response.success && response.data) {
        this.sessionId = response.data.sessionId || '';
        // Start polling for the result
        if (this.sessionId) {
          this.startPolling();
        } else {
          this.receiveError();
        }
      } else {
        this.receiveError(response.message || '');
      }
      this.getCreditDetail();
      this.isLoadingAPI.revise = false;
      return true;
    },

    async translateGenerateText(originalText = '', targetLanguage = '') {
      this.isLoadingAPI.translate = true;
      this.stopPolling();

      const payload = {
        sessionId : this.sessionId,
        language: this.translate.old || this.selectedLanguage || this.languageUse,
        fullText: originalText || '',
        selectedText: originalText || '',
        targetLanguage: targetLanguage || null,
      };
      const response = await aiGenerateService.postTranslateText(payload).catch(() => { return null });
      if (response && response.success && response.data) {
        this.sessionId = response.data.sessionId || '';
        if (this.sessionId) {
          this.startPolling();
        } else {
          this.receiveError();
        }
      } else {
        this.receiveError(response && response.message ? response.message : '');
      }
      this.getCreditDetail();
      this.isLoadingAPI.translate = false;
      return true;
    },
    startPolling() {
      this.pollForResult();
    },

    async pollForResult() {
      if (!this.sessionId) return;
      this.isLoadingAPI.detail = true;

      const response = await aiGenerateService.getGenerateTextDetail(this.sessionId).catch(() => { return null})
      if( response && response.data && response.success ) {
        const { lastedResult, promptList } = response.data;
        // Check if result is ready
        if (lastedResult) {
          this.stopPolling();
          this.receiveResponse(lastedResult);

          this.translate = {
            old : this.translate.new !== undefined ? this.translate.new : this.translate.old !== undefined ? this.translate.old : this.selectedLanguage || this.languageUse,
            new : undefined,
          }

          this.resetInputComponent();

          // Update suggestion tags from promptList if available
          if (Array.isArray(promptList) && promptList.length > 0) {
            this.suggestionTags = promptList.filter(item => item.type === 'suggestion');
            this.reviseTags = promptList.filter(item => item.type === 'revise');
          }
          this.isLoadingAPI.detail = false;
          return;
        }
        // Result not ready yet, poll again after interval
        this.pollingTimer = setTimeout(() => {
          this.pollForResult();
        }, this.POLLING_INTERVAL);
      }else{
        console.error('Polling error:', response.message);
        this.isLoadingAPI.detail = false;
        this.stopPolling();
        this.receiveError();
      }
    },

    /**
     * Stop the polling timer
     */
    stopPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer);
        this.pollingTimer = null;
      }
    },

    receiveResponse(responseText) {
      this.isLoading = false;
      this.messages.push({
        role: 'assistant',
        text: responseText,
        action: true
      });
      // After AI responds, switch to actions mode
      this.setControlMode('actions');
    },

    receiveError(errorMessage) {
      this.isLoading = false;
      this.messages.push({
        role: 'assistant',
        text: errorMessage || this.$t('engine.aiGenerate.lblError', this.languageEngine),
        action: false
      });
      // After error, switch to actions mode so user can retry
      this.setControlMode('actions');
    },

    handleCopy() {
      this.hasUsedMessage = true;
    },
    handleApply(payload) {
      this.hasUsedMessage = true;
      this.$emit('apply', {
        text: payload.text,
        context: this.actionContext
      });
    },

    handleClickOutside(event) {
      if (!this.$refs.modal) return;
      const modalEl = this.$refs.modal;
      const inModal = modalEl.contains(event.target);
      const inConfirmModal = event.target.closest('.modal-content') || event.target.closest('.modal-backdrop');
      if (inModal || inConfirmModal) return;
      event.stopPropagation();
      event.preventDefault();
      this.handleClose();
    },
    handleClose() {
      if (this._handleCloseCooldown && Date.now() - this._handleCloseCooldown < 300) return;
      this._handleCloseCooldown = Date.now();
      // Case 1: Still loading (AI is generating)
      // Case 2: Has AI response but user hasn't copied or applied yet
      const hasUnusedMessages = this.messages.some(msg => msg.role === 'assistant' && msg.action);
      if (this.isLoading || (hasUnusedMessages && !this.hasUsedMessage)) {
        this.$store.dispatch('designIframe/modals/setModal', {
          modal_id: 'modal-ai-close-confirm',
          payload: {},
          callback: {
            confirmed: () => {
              this.stopPolling();
              this.$emit('close');
            },
            cancelled: () => {},
          },
        });
        return;
      }
      this.stopPolling();
      this.$emit('close');
    },

    /**
     * Check if user has enough credits to proceed
     * @returns {boolean} true if credits are sufficient
     */
    hasEnoughCredits() {
      return this.creditAvailable > 0 && this.creditRemaining > 0 && this.creditRemaining > this.creditUsed;
    },
    /**
     * Show insufficient credit modal with credit data
     */
    showInsufficientCreditModal() {
      this.$store.dispatch('designIframe/modals/setModal', {
        modal_id: 'modal-ai-insufficient-credit',
        payload: {
          creditAvailable: this.creditAvailable,
          creditUsed: this.creditUsed,
          creditRemaining: this.creditRemaining,
        },
        callback: {},
      });
    },

    handleSuggestionClick(tag) {
      // Set selected tag for visual feedback
      this.selectedSuggestionTagId = tag.id;

      if (tag.action === 'fill_input') {
        // Fill input with prompt from API (or fallback to label)
        const text = tag.prompt || this.getTagLabel(tag);
        if (this.$refs.inputComponent) {
          this.$refs.inputComponent.setPromptText(text);
        }
      }
    },

    resetInputComponent() {
      if (this.$refs.inputComponent) {
        this.$refs.inputComponent.resetValidation();
        this.$refs.inputComponent.$refs.promptInput && this.$refs.inputComponent.$refs.promptInput.focus();
      }
    },

    handleToneSelected(tone) {
      this.selectedTone = tone.id;
    },

    handleActionClick(actionData) {
      const { type, tag } = actionData;
      this.actionType = type;
      const messagesUser = this.messages.filter(msg => msg.role === 'user')
      const messageLastUser = messagesUser.length ? messagesUser[messagesUser.length - 1] : null;
      switch (type) {
        case 'create':
          // Switch back to input mode
          this.setControlMode('input');
          this.actionSelect = 'create';
          this.selectedSuggestionTagId = null;
          this.fetchSuggestPrompt();
          this.$nextTick(() => {
            this.resetInputComponent();
          });
          this.sessionId = null;
          this.messages = [];
          this.messages.push({
            role: 'assistant',
            text: this.$t('engine.aiGenerate.lblDescription', this.languageEngine),
            isSystemMessage: true,
          });
          break;
        case 'translate':
          this.actionSelect = 'translate';
          if (tag) {
            // Use tag's prompt if available, otherwise use tag label
            const promptText = tag.prompt || this.getTagLabel(tag);
            const subLabel = this.getTagLabel(tag);
            const tagType = tag.id
            let targetLanguage = null;
            
            if(tagType === 'translate') {
              let subItemId = tag.subItemId
              if (Array.isArray(tag.data)) {
                const foundItem = tag.data.find(item => item.id === subItemId);
                targetLanguage = foundItem && foundItem.languageCode ? foundItem.languageCode : (this.languageUse || null);
              } else {
                targetLanguage = null;
              }
            }
            this.translate.new = targetLanguage;
            this.autoSendAction(promptText, subLabel, targetLanguage);
          } else {
            // Fallback to last user message if no tag provided
            this.autoSendAction(
              messageLastUser && messageLastUser.text ? messageLastUser.text : '',
              messageLastUser && messageLastUser.text ? messageLastUser.text : 'revise'
            );
          }
          break;
        case 'revise':
          if (tag) {
            // Use tag's prompt if available, otherwise use tag label
            const promptText = tag.prompt || this.getTagLabel(tag);
            const subLabel = this.getTagLabel(tag);

            this.autoSendAction(promptText, subLabel);
          } else {
            // Fallback to last user message if no tag provided
            this.autoSendAction(
              messageLastUser && messageLastUser.text ? messageLastUser.text : '',
              messageLastUser && messageLastUser.text ? messageLastUser.text : 'revise'
            );
          }
          break;
        default:
          break;
      }
    },

    scrollToBottom() {
      if (this.$refs.chatBody) {
        setTimeout(() => {
          this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight;
        }, 300);
      }
    },
    setControlMode(mode) {
      this.controlMode = mode;
    },
    // Drag Modal
    // drag & move https://www.w3schools.com/howto/howto_js_draggable.asp
    dragMouseDown(e) {
      e = e || window.event;
      e.preventDefault();
      // get the mouse cursor position at startup:
      this.pos3 = e.clientX;
      this.pos4 = e.clientY;
      document.onmouseup = this.closeDragElement;
      // call a function whenever the cursor moves:
      document.onmousemove = this.elementDrag;
    },
    elementDrag(e) {
      e = e || window.event;
      e.preventDefault();
      // calculate the new cursor position:
      this.pos1 = this.pos3 - e.clientX;
      this.pos2 = this.pos4 - e.clientY;
      this.pos3 = e.clientX;
      this.pos4 = e.clientY;
      // set the element's new position (same as Toolbar.vue):
      if (this.$refs.modal) {
        const position = {
          top: this.$refs.modal.offsetTop - this.pos2 + "px",
          left: this.$refs.modal.offsetLeft - this.pos1 + "px",
        };
        this.$emit('position-change', position);
      }
    },
    closeDragElement() {
      // stop moving when mouse button is released:
      document.onmouseup = null;
      document.onmousemove = null;
    },
    detectOutOfFrame(e) {
      if(e.clientY <= 0 || e.clientX <= 0 || (e.clientX >= window.innerWidth || e.clientY >= window.innerHeight))
      {
        this.closeDragElement()
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initModal();
      }
    },
    messages: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    }
  },
  beforeUnmount() {
    this.handleClose();
  },
  beforeDestroy() {
    this.stopPolling();
    document.removeEventListener("mouseleave", this.detectOutOfFrame);
    document.removeEventListener('mousedown', this.handleClickOutside, true);
    if (this._clickOutsideTimer) clearTimeout(this._clickOutsideTimer);
    this.closeDragElement();
  },
};
</script>

<style lang="scss">
.ai-generate-modal {

  .ai-generate-container {
    width: 500px;
    height: 100%;
    max-height: 800px;
    border-radius: 8px;
    box-shadow: 0 8px 32px 0 rgba(0, 53, 74, 0.12);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    white-space: normal;
  }

  // Body (chat area)
  .ai-generate-body {
    flex: 1;
    overflow-y: scroll;
    scrollbar-width: auto;
    border-radius: 4px;
    scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
    min-height: 132px;
    max-height: 600px;
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: rgba(0, 0, 0, 0.1);
    }
  }
  .ai-generate-control{
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
    background-color: #ffffff;
    box-shadow: -2px 4px 16px 0 rgba(40,190, 227, 0.15), 2px -4px 16px 0 rgba(238, 70, 188, 0.15);
    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>
